/**
 * Error classification utilities for the API and upload layers.
 *
 * Determines whether errors are transient (retryable) or permanent,
 * and provides structured classification for consistent error handling.
 *
 * Reference: audit-lite upload-error-classifier.ts
 */

export enum ErrorType {
  CANCELLED = 'CANCELLED',
  NETWORK = 'NETWORK',
  UNAUTHORIZED = 'UNAUTHORIZED',
  RATE_LIMITED = 'RATE_LIMITED',
  SERVER_ERROR = 'SERVER_ERROR',
  RETRY_EXCEEDED = 'RETRY_EXCEEDED',
  UNKNOWN = 'UNKNOWN'
}

/**
 * HTTP status codes that indicate a transient error worth retrying.
 */
const TRANSIENT_STATUS_CODES = new Set([429, 500, 502, 503]);

/**
 * Classifies an error based on its message content.
 *
 * Priority order (first match wins):
 *  1. CANCELLED — user-initiated, no retry
 *  2. NETWORK — connectivity issues, transient
 *  3. UNAUTHORIZED — permission issue, retry won't help
 *  4. RETRY_EXCEEDED — already exhausted retries
 *  5. UNKNOWN — fallback
 *
 * @param error - The error to classify
 * @returns The classified ErrorType
 */
export function classifyError(error: Error): ErrorType {
  const message = error.message?.toLowerCase() ?? '';

  if (/\bcancel(l?ed|lation)?\b/.test(message)) {
    return ErrorType.CANCELLED;
  }

  if (/\b(network|offline|connection)\b/.test(message)) {
    return ErrorType.NETWORK;
  }

  if (/\b(unauthorized|permission)\b/.test(message)) {
    return ErrorType.UNAUTHORIZED;
  }

  if (/\bretry\b/.test(message) || message.includes('limit exceeded')) {
    return ErrorType.RETRY_EXCEEDED;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Classifies an HTTP status code as a structured ErrorType.
 *
 * @param status - HTTP response status code
 * @returns The classified ErrorType
 */
export function classifyHttpStatus(status: number): ErrorType {
  if (status === 401 || status === 403) {
    return ErrorType.UNAUTHORIZED;
  }

  if (status === 429) {
    return ErrorType.RATE_LIMITED;
  }

  if (status >= 500 && status <= 503) {
    return ErrorType.SERVER_ERROR;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Determines if an error is transient and worth retrying.
 *
 * Transient errors: NETWORK, RATE_LIMITED, SERVER_ERROR
 * Non-transient: CANCELLED (user action), UNAUTHORIZED (permissions),
 *                RETRY_EXCEEDED (already exhausted), UNKNOWN (unsafe to retry)
 *
 * @param error - The error to evaluate
 * @returns True if the error is retryable
 */
export function isTransientError(error: Error): boolean {
  const type = classifyError(error);
  return type === ErrorType.NETWORK;
}

/**
 * Determines if an HTTP status code represents a transient error.
 *
 * @param status - HTTP response status code
 * @returns True if the status code is retryable
 */
export function isTransientStatus(status: number): boolean {
  return TRANSIENT_STATUS_CODES.has(status);
}
